import { DI } from '@intigral/constant/config/DIContainer';
import { container } from '@intigral/domain/lib/DataDomain';
import { IApiPathManager } from '@intigral/domain/lib/infrastructures/config/ApiPathManager';
import { useMediaQuery } from '@intigral/hooks';
import { FlexWrapper, GridSkeleton, Text } from '@intigral/shared';
import { MenuItem, MenuList } from '@intigral/shared/components/MenuList';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { FullVODCatalogList } from '../components/ui/FullVODCatalogList';
import h, { getCountry } from '../helpers/common';
import { fromBase64 } from '../helpers/formatter';
import { transformVOD } from '../utils/vodTransformer';
import { __ } from './../translations';
import { useConfigurationSections } from '../hooks/useConfiguration';

type VodListScreenProps = {
    screenName: string;
    screenKey: string;
};

export const VodListScreen: React.FC<VodListScreenProps> = ({ screenName, screenKey }) => {
    const urlParams = new URLSearchParams(window.location.search);
    const [content, setContent] = useState([]);
    const [currentFilter, setCurrentFilter] = useState<string>('');
    const [currentSort, setCurrentSort] = useState<string>('');
    const [searchParams, setSearchParams] = useSearchParams();
    // Using React Query instead of Redux for configuration
    const { config } = useConfigurationSections();
    const filters = urlParams.get('filters') ? JSON.parse(atob(urlParams.get('filters'))) : [];
    const sortingOptions = urlParams.get('sortingOptions') ? JSON.parse(atob(urlParams.get('sortingOptions'))) : [];
    const title = fromBase64(urlParams.get('title'));
    const [isLoading, setIsLoading] = useState(false);
    const isMobile = useMediaQuery('(max-width: 924px)');
    const getVodList = async (dataSource, sortingOption) => {
        setIsLoading(true);
        searchParams.set('filter_params', btoa(dataSource));
        searchParams.set('sort', sortingOption ? btoa(sortingOption.replace('sort=', '')) : btoa(''));
        setSearchParams(searchParams, { replace: true });
        try {
            const apiPathManager: IApiPathManager = container.resolve<IApiPathManager>(DI.UTILS.IApiPathManager);
            const url = apiPathManager.replacePlaceholders(dataSource);
            const mpxService = container.resolve<any>(DI.SERVICES.IMpxService);
            const response = await mpxService.getFeed(url, {
                byCustomValue: `{PC}{${getCountry()}}`,
                range: '0-10',
                sort: sortingOption ? sortingOption.replace('sort=', '') : '',
            });
            setContent(response?.entries);
            setIsLoading(false);
        } catch (error) {
            setIsLoading(false);
        }
    };

    const updateFilter = (item: any) => {
        const data = filters?.find((filter) => filter.filterName === item);
        setCurrentFilter(data?.title_resource_key || '');
    };

    const updateSortingOption = (item: any) => {
        const data = sortingOptions?.find((filter) => filter.key === item);
        setCurrentSort(data?.title_resource_key || '');
    };

    const getVodListApi = () => {
        if (filters && filters.length && sortingOptions && sortingOptions.length) {
            if (currentFilter && currentSort) {
                const filter = filters?.find((filter) => filter.title_resource_key === currentFilter);
                const sort = sortingOptions?.find((sort) => sort.title_resource_key === currentSort);
                getVodList(filter?.dataSources[0], sort?.value);
            }
        } else {
            if (currentFilter && filters && filters.length > 0) {
                const filter = filters?.find((filter) => filter.title_resource_key === currentFilter);
                const sort = sortingOptions?.find((sort) => sort.title_resource_key === currentSort);
                getVodList(filter?.dataSources[0], sort ? sort?.value : null);
            }
        }
    };

    useEffect(() => {
        if (filters && filters.length > 0) {
            updateFilter(filters[0]?.filterName);
        } else {
            setCurrentFilter('');
        }
        if (sortingOptions && sortingOptions.length > 0) {
            updateSortingOption(sortingOptions[0]?.key);
        } else {
            setCurrentSort('');
        }
    }, []);

    useEffect(() => {
        getVodListApi();
    }, [currentFilter, currentSort]);

    return (
        <div className="container full-width">
            <FlexWrapper
                flexDirection="row"
                wrap
                justifyContent="between"
                alignItems="center"
                gap="2x"
                paddingBottom="4x"
            >
                <Text as="h2" variant="h2" alignment="start" margin="0">
                    {__(title)}
                </Text>
                <FlexWrapper flexDirection="row" gap={{ default: '2x', md: '4x' }}>
                    {filters && filters.length > 0 ? (
                        <MenuList
                            selectedItem={currentFilter ? __(currentFilter) : ''}
                            triggerTitle={currentFilter ? __(currentFilter) : ''}
                            dropIcon="chevron-left"
                            align="end"
                            onChange={(e) => updateFilter(e)}
                        >
                            {filters.map((filter) => (
                                <MenuItem key={filter.filterName} title={filter.filterName} value={filter.filterName}>
                                    {__(filter.title_resource_key)}
                                </MenuItem>
                            ))}
                        </MenuList>
                    ) : (
                        <MenuList selectedItem="" triggerTitle="" dropIcon="chevron-left" align="end">
                            {[]}
                        </MenuList>
                    )}
                    {sortingOptions && sortingOptions.length > 0 ? (
                        <MenuList
                            selectedItem={currentSort ? __(currentSort) : ''}
                            triggerTitle={currentSort ? __(currentSort) : ''}
                            dropIcon="chevron-left"
                            align="end"
                            onChange={(e) => updateSortingOption(e)}
                            {...(isMobile && { width: 'fit-content' })}
                        >
                            {sortingOptions.map((sort) => (
                                <MenuItem key={sort.key} title={sort.key} value={sort.key}>
                                    {__(sort.title_resource_key)}
                                </MenuItem>
                            ))}
                        </MenuList>
                    ) : (
                        <MenuList
                            selectedItem=""
                            triggerTitle=""
                            dropIcon="chevron-left"
                            align="end"
                            {...(isMobile && { width: 'fit-content' })}
                        >
                            {[]}
                        </MenuList>
                    )}
                </FlexWrapper>
            </FlexWrapper>
            {isLoading ? (
                <GridSkeleton />
            ) : (
                <FullVODCatalogList content={content?.map((e) => transformVOD(e, config.promotionProviderImages))} />
            )}
        </div>
    );
};
